import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ProjectDetails } from '@/types/project';
import type { CostCentre } from '@/types/timesheet';
import type { DataMode } from '@/contexts/DataSourceContext';
import { StatusBadge, PriorityBadge } from '@/components/Pages/ActiveProjects/ProjectBadges';
import { OverviewTab } from '@/components/Pages/ActiveProjects/tabs/OverviewTab';
import { TeamTab } from '@/components/Pages/ActiveProjects/tabs/TeamTab';
import { TimelineTab } from '@/components/Pages/ActiveProjects/tabs/TimelineTab';
import { BudgetTab } from '@/components/Pages/ActiveProjects/tabs/BudgetTab';
import { TimeEntryTab } from '@/components/Pages/ActiveProjects/tabs/TimeEntryTab';

interface ProjectDetailsDialogProps {
  project: ProjectDetails;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  screenMode: DataMode;
  costCentres: CostCentre[];
  isLoadingCostCentres: boolean;
  isLoadingTeamMembers: boolean;
}

export const ProjectDetailsDialog: React.FC<ProjectDetailsDialogProps> = ({
  project,
  open,
  onOpenChange,
  screenMode,
  costCentres,
  isLoadingCostCentres,
  isLoadingTeamMembers,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono">
                  {project.code}
                </Badge>
                <StatusBadge status={project.status} />
                <PriorityBadge priority={project.priority} />
              </div>
              <DialogTitle className="text-2xl">{project.name}</DialogTitle>
              <DialogDescription>{project.description}</DialogDescription>
            </div>

          </div>
        </DialogHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="team">Team</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
            <TabsTrigger value="budget">Budget</TabsTrigger>
            <TabsTrigger value="time-entry">Time Entry</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 mt-4">
            <OverviewTab project={project} />
          </TabsContent>

          <TabsContent value="team" className="space-y-4 mt-4">
            <TeamTab project={project} isLoadingTeamMembers={isLoadingTeamMembers} />
          </TabsContent>

          <TabsContent value="timeline" className="space-y-4 mt-4">
            <TimelineTab project={project} />
          </TabsContent>

          <TabsContent value="budget" className="space-y-4 mt-4">
            <BudgetTab project={project} />
          </TabsContent>

          <TabsContent value="time-entry" className="space-y-4 mt-4">
            <TimeEntryTab
              project={project}
              screenMode={screenMode}
              costCentres={costCentres}
              isLoadingCostCentres={isLoadingCostCentres}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
